import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWithErrorHandling } from "../utils/service";

export const useFetchBookmarks = (
  sortCriteria,
  enabled = true,
  page = 0,
  filters = {}
) => {
  const searchParams = new URLSearchParams({
    sortField: sortCriteria.field,
    sortOrder: sortCriteria.direction,
    page: page,
  });
  // Reading list: read=true → read bookmarks, read=false → unread only
  if (filters.read !== undefined) {
    searchParams.set("read", filters.read);
  }
  // Favorites smart view: only favorited bookmarks
  if (filters.favorite !== undefined) {
    searchParams.set("favorite", filters.favorite);
  }
  // Tag filter: comma-separated tag names
  if (filters.tags) {
    searchParams.set("tags", filters.tags);
  }
  const params = searchParams.toString();

  return useQuery({
    queryKey: [
      "bookmarks",
      sortCriteria.field,
      sortCriteria.direction,
      page,
      filters.read,
      filters.favorite,
      filters.tags,
    ],
    queryFn: () => fetchWithErrorHandling(`/v1/bookmarks/list?${params}`),
    retry: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled,
    staleTime: 0, // Always consider data stale
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  });
};

// The user's tags (for the tag filter chips and the form's tag input).
export const useFetchTags = () => {
  return useQuery({
    queryKey: ["tags"],
    queryFn: () => fetchWithErrorHandling("/v1/bookmarks/tags"),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useFetchMetadata = () => {
  return useMutation({
    mutationFn: (url) =>
      fetchWithErrorHandling("/v1/bookmarks/metadata", {
        method: "POST",
        body: JSON.stringify({ url }),
      }),
  });
};

export const useVisitBookmark = () => {
  return useMutation({
    mutationFn: (uuid) =>
      fetchWithErrorHandling(`/v1/bookmarks/${uuid}/visit`, {
        method: "POST",
      }),
  });
};

export const useFetchBookmarkContent = (uuid, enabled = true) => {
  return useQuery({
    queryKey: ["bookmarkContent", uuid],
    queryFn: () => fetchWithErrorHandling(`/v1/bookmarks/${uuid}/content`),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled && !!uuid,
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
  });
};

export const useFetchBookmarkArchive = (uuid, enabled = true) => {
  return useQuery({
    queryKey: ["bookmarkArchive", uuid],
    queryFn: () => fetchWithErrorHandling(`/v1/bookmarks/${uuid}/archive`),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled && !!uuid,
    staleTime: 1000 * 60 * 30,
  });
};

export const useCreateArchive = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) =>
      fetchWithErrorHandling(`/v1/bookmarks/${uuid}/archive`, {
        method: "POST",
      }),
    onSuccess: (data, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["bookmarkArchive", uuid] });
    },
  });
};

export const useCreateBookmark = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookmarkData) =>
      fetchWithErrorHandling("/v1/bookmarks/create", {
        method: "POST",
        body: JSON.stringify(bookmarkData),
      }),
    onSuccess: (data, variables) => {
      // Invalidate the bookmarks query to refetch the list
      queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
      // Tags may have been created — refresh the tag list/filter.
      if (variables.tags && variables.tags.length > 0) {
        queryClient.invalidateQueries({ queryKey: ["tags"] });
      }

      // If collections were specified in the bookmark data, invalidate collection queries too
      if (variables.collections && variables.collections.length > 0) {
        // Invalidate all collection queries
        queryClient.invalidateQueries({ queryKey: ["collections"] });

        // Invalidate specific collection detail queries
        variables.collections.forEach((collectionUuid) => {
          queryClient.invalidateQueries({
            queryKey: ["collectionDetail", collectionUuid],
          });
        });
      }
    },
  });
};

export const useDeleteBookmark = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) =>
      fetchWithErrorHandling(`/v1/bookmarks/${uuid}/delete`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
      queryClient.invalidateQueries({ queryKey: ["collectionDetail"] });
    },
  });
};

export const useUpdateBookmark = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bookmarkData) =>
      fetchWithErrorHandling(`/v1/bookmarks/${bookmarkData.uuid}/update`, {
        method: "PUT",
        body: JSON.stringify(bookmarkData),
      }),
    onSuccess: (data, variables) => {
      // Invalidate the bookmarks query
      queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
      // Tags may have changed — refresh the tag list/filter.
      if (variables.tags !== undefined) {
        queryClient.invalidateQueries({ queryKey: ["tags"] });
      }

      // If collections were specified in the update, invalidate those queries
      if (variables.collections) {
        // Invalidate all collection queries
        queryClient.invalidateQueries({ queryKey: ["collections"] });

        // Invalidate specific collection detail queries
        variables.collections.forEach((collectionUuid) => {
          queryClient.invalidateQueries({
            queryKey: ["collectionDetail", collectionUuid],
          });
        });
      }
    },
  });
};

export const useFavoriteBookmark = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) =>
      fetchWithErrorHandling(`/v1/bookmarks/${uuid}/favorite`, {
        method: "PUT",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
    },
  });
};

// Bulk "move to collection": add many bookmarks to one collection in a single
// request. Backed by a dedicated join-table endpoint so bookmark metadata is
// never touched (a partial update would wipe it).
export const useAddBookmarksToCollection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bookmarkUuids, collectionUuid }) =>
      fetchWithErrorHandling("/v1/bookmarks/collections/add", {
        method: "POST",
        body: JSON.stringify({ bookmarkUuids, collectionUuid }),
      }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
      queryClient.invalidateQueries({ queryKey: ["collections"] });
      if (variables.collectionUuid) {
        queryClient.invalidateQueries({
          queryKey: ["collectionDetail", variables.collectionUuid],
        });
      }
    },
  });
};

export const useToggleRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) =>
      fetchWithErrorHandling(`/v1/bookmarks/${uuid}/read`, {
        method: "PUT",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
    },
  });
};

export const useRefreshBookmark = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) =>
      fetchWithErrorHandling(`/v1/bookmarks/${uuid}/refresh`, {
        method: "POST",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bookmarks"] });
      queryClient.invalidateQueries({ queryKey: ["collectionDetail"] });
    },
  });
};

export const useStartBackfill = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      fetchWithErrorHandling(`/v1/bookmarks/backfill`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["backfillStatus"] });
    },
  });
};

export const useBackfillStatus = (enabled = true) => {
  return useQuery({
    queryKey: ["backfillStatus"],
    queryFn: () => fetchWithErrorHandling(`/v1/bookmarks/backfill`),
    enabled,
    refetchOnWindowFocus: false,
    // Poll while a job is running; stop once it's idle/done/error.
    refetchInterval: (query) =>
      query.state.data?.data?.status === "running" ? 1500 : false,
  });
};
